import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { useForm, useFieldArray } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Modal } from '@/design-system/components/Modal';
import { FieldsEditor } from './FieldsEditor';
import { ReferenceSearchInput } from './ReferenceSearchInput';
import { useMasterDataList } from '@/lib/sdk/master-data/queries';
import { useFieldDefinitions } from '@/lib/sdk/master-data-field-definitions/queries';
import { useMasterDataCategories } from '@/lib/sdk/master-data-categories/queries';
import { resolveColumnConfig } from './CategoryManager';
import type { MasterDataRow } from '@/lib/sdk/master-data/types';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';
import type { MasterDataColumnConfig } from '@/lib/sdk/master-data-categories/types';

const ADD_NEW_SENTINEL = '__add_new__';

export type RowFormData = {
  dataset: string;
  key?: string;
  label: string;
  parent_key?: string;
  lat?: number;
  lng?: number;
  fields: { k: string; v: string }[];
};

// Create-mode starting values for a Fork: the source row's data with
// parent_key set to the source's key.
export type RowPrefill = {
  dataset: string;
  label: string;
  parent_key: string;
  parent_label: string;
  lat?: number;
  lng?: number;
  fields: Record<string, unknown>;
};

export function makeRowFormSchema(fieldDefs: MasterDataFieldDefinition[], existingKeys: string[] = []) {
  return z.object({
    dataset: z.string().min(1, 'Category is required'),
    key: z.string().optional().superRefine((val, ctx) => {
      if (!val) return;
      if (existingKeys.includes(val)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'This code is already in use — pick a different one' });
      }
    }),
    label: z.string().min(1, 'Name is required'),
    parent_key: z.string().optional(),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    fields: z.array(
      z.object({ k: z.string().min(1, 'Attribute name required'), v: z.string() })
        .superRefine((item, ctx) => {
          const def = fieldDefs.find(d => d.field_name === item.k);
          if (!def) return;
          const val = item.v.trim();
          if (def.required && val === '') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${def.label} is required`, path: ['v'] });
            return;
          }
          if (val === '') return;
          if (def.data_type === 'select' && def.options?.length) {
            if (!def.options.some(o => o.value === val))
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid option', path: ['v'] });
          } else if (def.data_type === 'number') {
            if (isNaN(Number(val)))
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a number', path: ['v'] });
          } else if (def.data_type === 'date') {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(val))
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be YYYY-MM-DD', path: ['v'] });
          } else if (def.data_type === 'boolean') {
            if (!['true', 'false', '1', '0'].includes(val.toLowerCase()))
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be true or false', path: ['v'] });
          }
        })
    ),
  });
}

function fieldsToKV(fields: Record<string, unknown>): { k: string; v: string }[] {
  return Object.entries(fields).map(([k, v]) => ({ k, v: String(v) }));
}

interface RowModalProps {
  isOpen: boolean;
  onClose: () => void;
  editRow?: MasterDataRow | null;
  prefill?: RowPrefill | null;
  defaultDataset?: string;
  onSubmit: (data: RowFormData) => void;
  isLoading: boolean;
  existingDatasets: string[];
  orgId: string;
  columnConfig: MasterDataColumnConfig;
  labelColumnName: string;
}

export function RowModal({
  isOpen,
  onClose,
  editRow,
  prefill,
  defaultDataset,
  onSubmit,
  isLoading,
  existingDatasets,
  orgId,
  columnConfig,
  labelColumnName,
}: RowModalProps) {
  const [isAddingNewDataset, setIsAddingNewDataset] = useState(false);
  // Create mode only — mirrors the "key" column CSV import already accepts (handler.go's
  // toSnakeCaseKey/InsertMasterDataRow path). Off by default so existing auto-generate
  // behavior is unchanged; the backend upserts on (org_id, dataset, key), so a manually
  // typed code that collides with an existing row would silently overwrite it — the
  // existingKeys check in makeRowFormSchema below is what actually prevents that, this
  // state only controls whether the input is shown.
  const [useCustomKey, setUseCustomKey] = useState(false);
  // FieldsEditor must not mount until the open-reset below has run: rendering it
  // against the previous session's (longer) fields array registers inputs like
  // fields.1.v that outlive the reset as phantom {v: ''} entries and fail validation.
  const [isFormReset, setIsFormReset] = useState(false);

  // Keep a ref to the latest resolver so RHF always validates against the current fieldDefs
  // without needing to re-create the form instance.
  const resolverRef = useRef<Resolver<RowFormData>>(zodResolver(makeRowFormSchema([])));

  const form = useForm<RowFormData>({
    resolver: (...args) => resolverRef.current(...args),
    defaultValues: editRow
      ? { dataset: editRow.dataset, label: editRow.label, parent_key: editRow.parent_key ?? '', lat: editRow.lat ?? undefined, lng: editRow.lng ?? undefined, fields: fieldsToKV(editRow.fields) }
      : { dataset: defaultDataset ?? '', label: '', parent_key: '', lat: undefined, lng: undefined, fields: [] },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = form;

  const { fields, append, remove } = useFieldArray({ control, name: 'fields' });
  const watchedDataset = watch('dataset');
  const prevDatasetRef = useRef(watchedDataset);
  const knownKeysRef = useRef<string[]>([]);

  // Reset form to correct values each time the modal opens
  useEffect(() => {
    if (!isOpen) {
      setIsFormReset(false);
      return;
    }
    if (editRow) {
      reset({ dataset: editRow.dataset, label: editRow.label, parent_key: editRow.parent_key ?? '', lat: editRow.lat ?? undefined, lng: editRow.lng ?? undefined, fields: fieldsToKV(editRow.fields) });
    } else if (prefill) {
      // Sync the ref first, or the dataset-change effect below treats the
      // prefilled dataset as a user switch and wipes the prefilled fields.
      prevDatasetRef.current = prefill.dataset;
      reset({
        dataset: prefill.dataset,
        key: '',
        label: prefill.label,
        parent_key: prefill.parent_key,
        lat: prefill.lat,
        lng: prefill.lng,
        fields: fieldsToKV(prefill.fields),
      });
    } else {
      reset({
        dataset: defaultDataset ?? '',
        key: '',
        label: '',
        parent_key: '',
        lat: undefined,
        lng: undefined,
        fields: knownKeysRef.current.map(k => ({ k, v: '' })),
      });
    }
    setIsAddingNewDataset(false);
    setUseCustomKey(false);
    setIsFormReset(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (prevDatasetRef.current !== watchedDataset) {
      prevDatasetRef.current = watchedDataset;
      if (!editRow) {
        // Clear fields so knownKeys effect can re-populate for the new dataset
        setValue('fields', []);
        // A code typed for one dataset has no meaning in another
        setValue('key', '');
        setUseCustomKey(false);
      }
    }
  }, [watchedDataset, editRow, setValue]);

  // Fetch existing rows for the selected dataset — used to derive known attribute keys
  const { data: datasetRows } = useMasterDataList(
    orgId,
    watchedDataset && watchedDataset !== ADD_NEW_SENTINEL
      ? { dataset: watchedDataset, page: 1, page_size: 200 }
      : undefined,
  );

  const validDataset = watchedDataset && watchedDataset !== ADD_NEW_SENTINEL ? watchedDataset : '';
  const { data: fieldDefs = [] } = useFieldDefinitions(orgId, validDataset);
  const managedRefDef = fieldDefs.find(d => d.data_type === 'managed_reference');
  const watchedParentKey = watch('parent_key');
  // managed_reference lives in parent_key, not the fields JSONB, so it's excluded
  // from every fields-array-driven view (validation, known keys, auto-populate).
  const additionalInfoFieldDefs = useMemo(
    () => fieldDefs.filter(d => d.data_type !== 'managed_reference'),
    [fieldDefs]
  );

  // The modal can target a different category than the page filter, so resolve the
  // config from the category actually selected here rather than trusting the prop.
  const { data: categories = [] } = useMasterDataCategories(orgId);
  const selectedCategory = categories.find((c) => c.name === validDataset);
  const config = selectedCategory ? resolveColumnConfig(selectedCategory) : columnConfig;
  const nameColumnLabel = selectedCategory?.label_column_name ?? labelColumnName;

  // Existing codes for the selected dataset — checked client-side so a manually typed
  // code that collides with another row can't silently overwrite it (the backend
  // upserts on (org_id, dataset, key); see the useCustomKey comment above).
  const existingKeys = useMemo(
    () => (editRow ? [] : (datasetRows?.items ?? []).map((row) => row.key)),
    [editRow, datasetRows]
  );

  // Columns this category hides are not rendered, so submit the stored values
  // untouched instead of the auto-populated blanks sitting in the form state.
  const handleFormSubmit = (data: RowFormData) => {
    onSubmit({
      ...data,
      key: !editRow && useCustomKey ? data.key?.trim() || undefined : undefined,
      parent_key: config.show_parent_key ? data.parent_key : (editRow?.parent_key ?? ''),
      lat: config.show_location ? data.lat : (editRow?.lat ?? undefined),
      lng: config.show_location ? data.lng : (editRow?.lng ?? undefined),
      fields: config.show_additional_info ? data.fields : fieldsToKV(editRow?.fields ?? {}),
    });
  };

  // Sync resolver with the latest fieldDefs/existingKeys so validation rules stay current.
  useEffect(() => {
    resolverRef.current = zodResolver(makeRowFormSchema(additionalInfoFieldDefs, existingKeys));
  }, [additionalInfoFieldDefs, existingKeys]);

  // Derive unique known keys from existing rows' fields, excluding hidden fields in add mode
  const knownKeys = useMemo(() => {
    const hiddenFieldNames = new Set(
      editRow ? [] : additionalInfoFieldDefs.filter(d => d.hidden).map(d => d.field_name)
    );
    const keys = new Set<string>();
    for (const row of (datasetRows?.items ?? [])) {
      for (const k of Object.keys(row.fields)) {
        if (!hiddenFieldNames.has(k)) keys.add(k);
      }
    }
    return [...keys].sort();
  }, [datasetRows, additionalInfoFieldDefs, editRow]);
  knownKeysRef.current = knownKeys;

  // Auto-populate attribute rows from known keys when data loads after modal is already open
  // knownKeys already excludes hidden fields in add mode, so this naturally omits them
  useEffect(() => {
    if (editRow) return;
    if (knownKeys.length === 0) return;
    const current = form.getValues('fields');
    if (current.length === 0) {
      setValue('fields', knownKeys.map(k => ({ k, v: '' })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownKeys]);

  // When editing, merge any newly added managed field definitions that aren't yet in the row
  useEffect(() => {
    if (!isOpen || !editRow || additionalInfoFieldDefs.length === 0) return;
    const current = form.getValues('fields');
    const currentKeys = new Set(current.map(f => f.k));
    const toAdd = additionalInfoFieldDefs
      .filter(def => !currentKeys.has(def.field_name))
      .map(def => ({ k: def.field_name, v: String(editRow.fields[def.field_name] ?? '') }));
    if (toAdd.length > 0) setValue('fields', [...current, ...toAdd]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalInfoFieldDefs, isOpen]);

  const handleClose = () => {
    reset();
    setIsAddingNewDataset(false);
    onClose();
  };

  const showDatasetInput = isAddingNewDataset || existingDatasets.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editRow ? 'Edit Item' : prefill ? 'Fork Item' : 'Add Item'}
      size="lg"
      scrollBehavior="inside"
      footer={
        <HStack spacing={3} w="full" justify="flex-end">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme="brand" isLoading={isLoading} onClick={handleSubmit(handleFormSubmit)} data-testid="md-row-submit-btn">
            {editRow ? 'Save' : 'Create'}
          </Button>
        </HStack>
      }
    >
      <VStack spacing={4} align="stretch">
        {/* Tip banner */}
        <HStack
          spacing={2}
          p={3}
          borderRadius="md"
          bg="bg-muted"
          borderWidth="1px"
          borderColor="border-default"
          align="flex-start"
        >
          <Box color="brand.500" mt="1px" flexShrink={0}>
            <FiInfo size={14} />
          </Box>
          <Text fontSize="xs" color="text-secondary" lineHeight="tall">
            Each item needs a <strong>Category</strong> (the data group, e.g. <em>orders</em>) and a{' '}
            <strong>Name</strong> (the human-readable label). A unique code is auto-generated. Use{' '}
            <strong>Additional Info</strong> to store any extra fields you need — those values will be
            available as <code>{'{task_N_attr:field_name}'}</code> in linked workflows.
          </Text>
        </HStack>

        {/* Category */}
        <FormControl isInvalid={!!errors.dataset} isRequired>
          <FormLabel color="text-primary" mb={1}>
            Category
          </FormLabel>
          <Text fontSize="xs" color="text-secondary" mb={2}>
            The data group this item belongs to (e.g. <em>orders</em>, <em>machines</em>, <em>parts</em>)
          </Text>

          {editRow || prefill ? (
            <Input
              value={editRow?.dataset ?? prefill?.dataset}
              isReadOnly
              bg="bg-muted"
              borderColor="border-default"
              _hover={{ borderColor: 'border-default' }}
              color="text-secondary"
              data-testid="md-row-dataset-readonly"
            />
          ) : showDatasetInput ? (
            <HStack spacing={2}>
              <Input
                {...register('dataset')}
                placeholder="e.g. orders"
                bg="bg-surface"
                borderColor="border-default"
                _hover={{ borderColor: 'border-hover' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                data-testid="md-row-dataset-input"
              />
              {existingDatasets.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingNewDataset(false);
                    setValue('dataset', existingDatasets[0]);
                  }}
                  flexShrink={0}
                >
                  Pick existing
                </Button>
              )}
            </HStack>
          ) : (
            <Select
              {...register('dataset', {
                onChange: (e) => {
                  if (e.target.value === ADD_NEW_SENTINEL) {
                    setIsAddingNewDataset(true);
                    setValue('dataset', '');
                  }
                },
              })}
              bg="bg-surface"
              borderColor="border-default"
              _hover={{ borderColor: 'border-hover' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
              data-testid="md-row-dataset-select"
            >
              <option value="">Select a category…</option>
              {existingDatasets.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
              <option value={ADD_NEW_SENTINEL}>＋ Add new category</option>
            </Select>
          )}
          <FormErrorMessage>{errors.dataset?.message}</FormErrorMessage>
        </FormControl>

        {/* Code — read-only on edit, auto-generated on create */}
        {!config.show_code ? null : editRow ? (
          <FormControl>
            <FormLabel color="text-primary" mb={1}>
              Code
            </FormLabel>
            <Text fontSize="xs" color="text-secondary" mb={2}>
              Auto-generated unique identifier — cannot be changed
            </Text>
            <Input
              value={editRow.key}
              isReadOnly
              bg="bg-muted"
              borderColor="border-default"
              _hover={{ borderColor: 'border-default' }}
              color="text-secondary"
              fontFamily="mono"
              data-testid="md-row-code-display"
            />
          </FormControl>
        ) : (
          <FormControl isInvalid={!!errors.key}>
            <FormLabel color="text-primary" mb={1}>
              Code
            </FormLabel>
            <Checkbox
              isChecked={useCustomKey}
              onChange={(e) => {
                setUseCustomKey(e.target.checked);
                if (!e.target.checked) setValue('key', '');
              }}
              colorScheme="brand"
              mb={2}
              data-testid="md-row-use-custom-key"
            >
              Enter a custom code
            </Checkbox>
            {useCustomKey ? (
              <>
                <Input
                  {...register('key')}
                  placeholder="e.g. +919900000001"
                  bg="bg-surface"
                  borderColor="border-default"
                  _hover={{ borderColor: 'border-hover' }}
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  fontFamily="mono"
                  data-testid="md-row-key-input"
                />
                <FormErrorMessage>{errors.key?.message}</FormErrorMessage>
              </>
            ) : (
              <Box
                p={3}
                borderRadius="md"
                bg="bg-muted"
                borderWidth="1px"
                borderColor="border-default"
              >
                <Text fontSize="xs" color="text-secondary">
                  A unique identifier (e.g. <em>O-000123</em>) will be auto-generated when you
                  save.
                </Text>
              </Box>
            )}
          </FormControl>
        )}

        {/* Name */}
        <FormControl isInvalid={!!errors.label} isRequired isReadOnly={!config.edit_label}>
          <FormLabel color="text-primary" mb={1}>
            {nameColumnLabel}
          </FormLabel>
          <Text fontSize="xs" color="text-secondary" mb={2}>
            Human-readable label shown to users (e.g. <em>Drill Press Assembly Order</em>)
          </Text>
          <Input
            {...register('label')}
            placeholder="e.g. Drill Press Assembly Order"
            bg={config.edit_label ? 'bg-surface' : 'bg-muted'}
            borderColor="border-default"
            _hover={{ borderColor: config.edit_label ? 'border-hover' : 'border-default' }}
            _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
            isReadOnly={!config.edit_label}
            data-testid="md-row-label-input"
          />
          <FormErrorMessage>{errors.label?.message}</FormErrorMessage>
        </FormControl>

        {/* Belongs To (parent_key) — a managed_reference field definition swaps this for a
            custom-labeled search against the dataset it's configured to reference. */}
        {config.show_parent_key && (
          <FormControl isInvalid={!!errors.parent_key} isReadOnly={!config.edit_parent_key}>
            <FormLabel color="text-primary" mb={1}>
              {managedRefDef ? managedRefDef.label : 'Belongs To'}
            </FormLabel>
            <Text fontSize="xs" color="text-secondary" mb={2}>
              {managedRefDef ? (
                <>Search for the {managedRefDef.label.toLowerCase()} this item belongs to. Leave blank if it has none.</>
              ) : (
                <>Code of the parent item this one rolls up to (e.g. <em>O-000123</em>). Leave blank if it has no parent.</>
              )}
            </Text>
            {managedRefDef && config.edit_parent_key ? (
              <ReferenceSearchInput
                id="row-parent-key"
                orgId={orgId}
                dataset={(managedRefDef.validation?.reference_dataset as string) ?? ''}
                valueField={managedRefDef.validation?.value_field === 'label' ? 'label' : 'key'}
                value={watchedParentKey ?? ''}
                onChange={(v) => setValue('parent_key', v, { shouldValidate: true })}
                placeholder={`Search ${managedRefDef.label}…`}
                error={errors.parent_key?.message}
              />
            ) : (
              <Input
                {...register('parent_key')}
                placeholder="e.g. O-000123"
                bg={config.edit_parent_key ? 'bg-surface' : 'bg-muted'}
                borderColor="border-default"
                _hover={{ borderColor: config.edit_parent_key ? 'border-hover' : 'border-default' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                fontFamily="mono"
                isReadOnly={!config.edit_parent_key || !!managedRefDef}
                data-testid="md-row-parent-key-input"
              />
            )}
            {prefill && (
              <Text fontSize="xs" color="text-secondary" mt={1} data-testid="md-row-fork-note">
                Child of <strong>{prefill.parent_label}</strong> ({prefill.parent_key})
              </Text>
            )}
            <FormErrorMessage>{errors.parent_key?.message}</FormErrorMessage>
          </FormControl>
        )}

        {/* Location (lat/lng) */}
        {config.show_location && (
          <HStack spacing={3} align="flex-start">
            <FormControl isInvalid={!!errors.lat} isReadOnly={!config.edit_location}>
              <FormLabel color="text-primary" mb={1}>
                Latitude
              </FormLabel>
              <Input
                type="number"
                step="any"
                {...register('lat', { valueAsNumber: true })}
                placeholder="e.g. 10.0159"
                bg={config.edit_location ? 'bg-surface' : 'bg-muted'}
                borderColor="border-default"
                _hover={{ borderColor: config.edit_location ? 'border-hover' : 'border-default' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                isReadOnly={!config.edit_location}
                data-testid="md-row-lat-input"
              />
              <FormErrorMessage>{errors.lat?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.lng} isReadOnly={!config.edit_location}>
              <FormLabel color="text-primary" mb={1}>
                Longitude
              </FormLabel>
              <Input
                type="number"
                step="any"
                {...register('lng', { valueAsNumber: true })}
                placeholder="e.g. 76.3419"
                bg={config.edit_location ? 'bg-surface' : 'bg-muted'}
                borderColor="border-default"
                _hover={{ borderColor: config.edit_location ? 'border-hover' : 'border-default' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                isReadOnly={!config.edit_location}
                data-testid="md-row-lng-input"
              />
              <FormErrorMessage>{errors.lng?.message}</FormErrorMessage>
            </FormControl>
          </HStack>
        )}

        {/* Additional Info (fields) */}
        {config.show_additional_info && isFormReset && (
          <FieldsEditor
            fields={fields}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            append={append}
            remove={remove}
            knownKeys={knownKeys}
            fieldDefs={additionalInfoFieldDefs}
            orgId={orgId}
            rowId={editRow?.id}
            rowKey={editRow?.key}
            dataset={validDataset}
            isReadOnly={!config.edit_additional_info}
          />
        )}
      </VStack>
    </Modal>
  );
}
